import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, RotateCcw, ArrowLeft, Eye, Lock } from "lucide-react";
import useSiteConfig from "@/hooks/useSiteConfig";

const FIELDS = [
  { section: "brand", label: "Brand name", key: "name" },
  { section: "brand", label: "Short name", key: "shortName" },
  { section: "brand", label: "Tagline", key: "tagline" },
  { section: "brand", label: "Chatbot name", key: "chatbotName" },
  { section: "contact", label: "Email", key: "email", type: "email" },
  { section: "contact", label: "Phone", key: "phone" },
  { section: "contact", label: "Office address", key: "address" },
  { section: "contact", label: 'Booking URL ("Book Strategy Call")', key: "bookingUrl" },
  { section: "social", label: "LinkedIn URL", key: "linkedin" },
  { section: "social", label: "Twitter / X URL", key: "twitter" },
  { section: "social", label: "Instagram URL", key: "instagram" },
  { section: "social", label: "YouTube URL", key: "youtube" },
];

const PASS_KEY = "qsm_admin_pass";
const DEFAULT_PASS = "quantum2025"; // change in /admin once unlocked

export default function AdminConfig() {
  const { config, save, reset } = useSiteConfig();
  const [draft, setDraft] = useState(config);
  const [unlocked, setUnlocked] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("qsm_admin_unlocked") === "yes"
  );
  const [passInput, setPassInput] = useState("");

  const update = (section, key, value) => {
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));
  };

  const onSave = () => {
    save({
      brand: draft.brand,
      contact: draft.contact,
      social: draft.social,
      hero: draft.hero,
      legal: draft.legal,
    });
    toast.success("Saved. Changes are live on this browser.");
  };

  const onReset = () => {
    reset();
    setDraft(config);
    toast.success("Reverted to file defaults.");
  };

  const unlock = (e) => {
    e.preventDefault();
    const stored = window.localStorage.getItem(PASS_KEY) || DEFAULT_PASS;
    if (passInput === stored) {
      window.localStorage.setItem("qsm_admin_unlocked", "yes");
      setUnlocked(true);
    } else {
      toast.error("Incorrect passcode.");
    }
  };

  if (!unlocked) {
    return (
      <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <form
          onSubmit={unlock}
          className="glass-strong rounded-3xl p-8 w-full max-w-md"
          data-testid="admin-unlock-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center glow-cyan">
              <Lock size={16} className="text-black" />
            </div>
            <div>
              <div className="font-clash text-lg font-medium">Admin Access</div>
              <div className="text-xs text-zinc-500">Quantum Star Marketing — site editor</div>
            </div>
          </div>
          <label className="text-[11px] font-mono-acc uppercase tracking-[0.25em] text-zinc-500">Passcode</label>
          <input
            type="password"
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            placeholder="quantum2025"
            className="mt-2 w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-400/50"
            data-testid="admin-pass-input"
            autoFocus
          />
          <button
            type="submit"
            className="mt-5 w-full inline-flex items-center justify-center rounded-xl bg-white text-black py-3 text-sm font-medium hover:scale-[1.02] active:scale-[0.98] transition-transform"
            data-testid="admin-unlock-submit"
          >
            Unlock Editor
          </button>
          <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
            Default passcode is <code className="text-cyan-300">quantum2025</code>. After unlocking, change it from the
            "Security" section below.
          </p>
          <Link to="/" className="mt-5 inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white">
            <ArrowLeft size={12} /> Back to site
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050505] text-white py-16" data-testid="admin-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-2">/ Admin</div>
            <h1 className="font-clash text-3xl md:text-4xl font-semibold tracking-tight">Site Editor</h1>
            <p className="mt-2 text-sm text-zinc-400 max-w-xl">
              Edit contact details, social links and brand copy. Changes save instantly on this browser. For permanent
              changes across all visitors, edit{" "}
              <code className="text-cyan-300">src/config/siteConfig.js</code> and redeploy.
            </p>
          </div>
          <Link
            to="/"
            className="hidden sm:inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs text-zinc-300 hover:text-white"
          >
            <Eye size={13} /> View site
          </Link>
        </div>

        <div className="space-y-4">
          {FIELDS.map((f) => (
            <div key={`${f.section}-${f.key}`} className="rounded-2xl glass p-4">
              <label className="text-[10px] font-mono-acc uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                {f.section} · {f.label}
              </label>
              <input
                type={f.type || "text"}
                value={draft[f.section]?.[f.key] ?? ""}
                onChange={(e) => update(f.section, f.key, e.target.value)}
                className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-400/40 transition-colors"
                data-testid={`admin-${f.section}-${f.key}`}
              />
            </div>
          ))}

          <div className="rounded-2xl glass p-4">
            <label className="text-[10px] font-mono-acc uppercase tracking-[0.25em] text-zinc-500 block mb-2">
              SECURITY · Change passcode
            </label>
            <ChangePass />
          </div>
        </div>

        <div className="sticky bottom-4 mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={onSave}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black px-6 py-3 text-sm font-medium hover:scale-[1.02] active:scale-[0.98] transition-transform"
            data-testid="admin-save"
          >
            <Save size={14} /> Save changes
          </button>
          <button
            onClick={onReset}
            className="inline-flex items-center justify-center gap-2 rounded-xl glass-strong px-6 py-3 text-sm hover:bg-white/10 transition-colors"
            data-testid="admin-reset"
          >
            <RotateCcw size={14} /> Reset to file defaults
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 px-6 py-3 text-sm hover:bg-white/5 transition-colors"
          >
            <Eye size={14} /> Preview site
          </Link>
        </div>
      </div>
    </main>
  );
}

function ChangePass() {
  const [next, setNext] = useState("");
  return (
    <div className="flex gap-2">
      <input
        type="text"
        value={next}
        onChange={(e) => setNext(e.target.value)}
        placeholder="New passcode"
        className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-sm outline-none focus:border-purple-400/40"
      />
      <button
        type="button"
        onClick={() => {
          if (!next || next.length < 4) {
            toast.error("Use at least 4 characters.");
            return;
          }
          window.localStorage.setItem(PASS_KEY, next);
          toast.success("Passcode updated.");
          setNext("");
        }}
        className="rounded-xl bg-white text-black px-5 text-sm font-medium hover:scale-[1.02] transition-transform"
      >
        Update
      </button>
    </div>
  );
}
